/**
 * What a planet LOOKS like, derived from its ring and its name.
 *
 * The reference field hand-authored all 34 of its worlds: a
 * `switch (planet.id)` for the motif and a literal `{hi, base, lo}`
 * table for the colour.  That cannot survive contact with a real
 * registry, where planets are authored in a taxonomy file and a new one
 * appears the day somebody uploads it — a hand-drawn atlas would render
 * every unlisted planet as a grey circle.
 *
 * So the look is DERIVED, and derived from exactly two things:
 *
 *   the RING   decides the planet's CLASS.  The reference calls this a
 *              celestial ladder — rocky worlds near the sun, then living
 *              worlds, giants and ringed exotics furthest out — and it is
 *              what makes a ring read as one family at a glance.  One
 *              world in six runs MOLTEN instead, which is the sky's only
 *              exception and is still a disc: see below.
 *
 *              "The ring" here means WHERE IT IS DRAWN — its slot, the
 *              nth orbit out — not the number an author gave it.  Levels
 *              are an ordering and may be any integers at all (0, 5, 9 is
 *              a perfectly good atlas); slots are 0, 1, 2 by construction.
 *              Keying the look on the raw level would hue a planet from
 *              one anchor while the ring it sits on is accented from
 *              another, so a green ring would hold blue worlds.
 *   the NAME   decides everything within that class: the hue inside the
 *              ring's band, the motif on the body, the outer adornment,
 *              the tilt.
 *
 * Both are pure functions of stable ids, so a planet's appearance is
 * the same on every device, in every session, forever — and moving a
 * planet to another ring re-classes it, which is the honest behaviour:
 * it really is somewhere else now.
 *
 * Nothing here reads learner state.  State changes how a body is
 * PAINTED (cold, dimmed, glowing) but never what it IS — a planet a
 * learner has never opened must be recognisably the same world once
 * they have.
 */

/**
 * Every class is a DISC.  That is the whole rule, and it is what keeps a
 * sky of forty worlds calm: they differ in colour, in the motif on the
 * body and in what orbits them, never in silhouette.  ``molten`` is the
 * one exception and it breaks no part of that — a molten world is an
 * ordinary disc with a darkened crust and two glowing seams over its
 * motif, so it reads as hot rather than as a different kind of shape.
 *
 * (It replaces the reference's STAR, which drew a four-pointed glyph
 * with no disc, no motif and no interior at any zoom — three in ten
 * worlds rendered as a spark rather than as a world.)
 */
export type PlanetClass = "rocky" | "living" | "giant" | "ringed" | "molten";
export type Adornment = "none" | "rings" | "tilted-pair" | "moon" | "rays" | "halo";

export type PlanetLook = {
  planetClass: PlanetClass;
  /** Body gradient stops, light → mid → dark. */
  hi: string;
  base: string;
  lo: string;
  /** A contrasting accent used by motifs that want a hot spot. */
  spot: string;
  motif: MotifKey;
  adornment: Adornment;
  /** Degrees, for adornments that tilt — and, halved, for a molten world's seams. */
  tilt: number;
};

// ── the hash ─────────────────────────────────────────────────────────
//
// FNV-1a, 32-bit, unsigned.  Chosen because it is four lines, has no
// dependencies, and — the property that matters here — is defined by a
// public constant rather than by whatever this file happens to do, so
// the Python side, a test, or a future renderer can reproduce a
// planet's look without importing this module.

export function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** A stable 0..n-1 draw from one seed, decorrelated per `salt`. */
const draw = (seed: number, salt: string, n: number) =>
  n <= 0 ? 0 : hash32(salt + ":" + seed.toString(36)) % n;

// ── the ladder ───────────────────────────────────────────────────────
//
// Ring level → class.  The ladder saturates rather than wrapping: an
// atlas with nine rings does not go back to rocky worlds at ring 5,
// because "further out" has to keep meaning "stranger" or the map stops
// teaching anything.  Molten is the exception to the ladder and grows
// outward, where the field's own strangeness does.

const LADDER: PlanetClass[] = ["rocky", "living", "giant", "ringed"];

/**
 * Percent of a ring's worlds that run hot.
 *
 * Deliberately small, and smallest where the sky should be calm.  An
 * exception at a third is not an exception, it is a second rule, and a
 * sky where that many worlds shout has no emphasis left to spend.  The
 * rate rises outward because that is where the field's own strangeness
 * is — which is the one thing this lottery is allowed to mean.
 */
const moltenShare = (level: number) => Math.min(32, 14 + 7 * level);

export function planetClass(planetId: string, ringSlot: number | null): PlanetClass {
  // An unplaced planet is a wanderer: it gets the innermost class, so it
  // draws as an ordinary rocky world rather than inventing a rank the
  // author never gave it.
  const level = ringSlot === null ? 0 : Math.max(0, ringSlot);
  const seed = hash32(planetId);
  if (draw(seed, "molten", 100) < moltenShare(level)) return "molten";
  return LADDER[Math.min(level, LADDER.length - 1)];
}

// ── colour ───────────────────────────────────────────────────────────
//
// Each ring owns a hue ANCHOR (the same hues the ring accents use, so a
// planet belongs to its ring by colour as well as by position) and each
// planet takes a hue inside a ±52° band around it.  The band is wide
// enough that two planets on one ring never read as the same world and
// narrow enough that the ring still reads as one family.

const RING_HUE = [128, 212, 35, 275];

/**
 * HSL in, HEX out — and the "out" half is the load-bearing one.
 *
 * These colours are pasted into strings that append a two-digit alpha
 * (`${colour}AA` in a `drop-shadow`, a border, a box-shadow) — the
 * reference field's own idiom, and it works because its palette was
 * hand-written hex.  A function-notation colour makes that string
 * invalid, and CSS drops an invalid declaration WHOLE and SILENTLY: the
 * glow simply never renders and nothing says why.  So the derivation
 * thinks in HSL, because that is how you spread hues around a ring, and
 * hands back hex, because that is what the rest of the drawing expects.
 */
function hslHex(h: number, s: number, l: number): string {
  const hue = (((h % 360) + 360) % 360) / 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;
  if (sat === 0) {
    const grey = Math.round(light * 255);
    return `#${[grey, grey, grey].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  }
  const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
  const p = 2 * light - q;
  const channel = (t: number) => {
    let value = t;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };
  return `#${[channel(hue + 1 / 3), channel(hue), channel(hue - 1 / 3)]
    .map((v) => Math.round(v * 255).toString(16).padStart(2, "0"))
    .join("")}`;
}

const hsl = hslHex;

function palette(planetId: string, ringSlot: number | null, planetClass: PlanetClass) {
  const seed = hash32(planetId);
  const level = ringSlot === null ? 0 : Math.max(0, ringSlot);
  const anchor = RING_HUE[level % RING_HUE.length];
  const hue = anchor + (draw(seed, "hue", 105) - 52);

  // A molten world keeps its ring's hue — it belongs to its ring like
  // every other world — and is painted DEEPER, so the seams over it read
  // as the brightest thing on the body.  It is the only class that
  // touches the palette at all; the rest differ by motif and adornment.
  const isMolten = planetClass === "molten";
  const sat = 32 + draw(seed, "sat", 34);
  const light = (isMolten ? 38 : 56) + draw(seed, "light", isMolten ? 11 : 18);

  return {
    hi: hsl(hue + 6, sat + 16, Math.min(95, light + 25)),
    base: hsl(hue, sat, light),
    lo: hsl(hue - 9, sat + 10, Math.max(18, light - 24)),
    // The accent separates from the body, which is what a "hot spot" has
    // to do to be one.  On a molten world it is an EMBER — a fixed warm
    // band whatever the ring's hue, so every molten world reads hot and
    // they read as one family across the sky.
    spot: isMolten
      ? hsl(22 + draw(seed, "ember", 20), 82, 60)
      : hsl(hue + 150 + draw(seed, "spot", 60), sat + 24, Math.max(38, light - 14)),
  };
}

// ── the motif library ────────────────────────────────────────────────
//
// Transcribed from the reference's `motifInner` switch, one entry per
// distinct visual idea rather than one per planet: bands, an equator, a
// terminator, strata, speckles, and so on.  Which one a planet wears is
// its name's draw, so the vocabulary is the reference's and the
// assignment is the registry's.

export const MOTIFS = [
  "bands",       // three curved latitude bands
  "equator",     // a tilted meridian ellipse plus an equator line
  "eyes",        // concentric circles with a filled pupil
  "terminator",  // a shadowed half and a bright edge
  "hex",         // a hexagon with a spine
  "speckles",    // scattered detector dots
  "zigzag",      // a descending polyline
  "target",      // rings with an off-centre true mark
  "strata",      // stacked horizontal layers of varying weight
  "cutbar",      // a filled bar, a faint bar, and the cut between them
  "grid",        // two verticals, one horizontal, nodes at the joints
  "shelves",     // two soft shelves and a lens
  "rules",       // four ruled lines of alternating weight
  "crescent",    // a crescent carved from the disc
  "split",       // a warm half against a cool half
  "stream",      // a bright band running the width
  "histogram",   // four bars and a drawn sample
  "window",      // a framed window with three tokens inside
  "mask",        // a face circle with a mask over it
  "twin",        // two overlapping discs, one dashed
  "flow",        // three flow lines bending toward one point
  "ladder",      // two rails and four rungs
  "gauge",       // a dial arc, a needle, and three ticks
  "wire",        // a stepped trace between two terminals
  "seams",       // two irregular cuts running pole to pole
  "wrapper",     // a dashed enclosure around a solid core
] as const;

export type MotifKey = (typeof MOTIFS)[number];

// ── outer adornment ──────────────────────────────────────────────────
//
// What orbits, encircles or radiates from the body.  A ringed world
// always gets a ring system — that is what makes it that class — and
// everything else draws from the reference's other outer forms, most
// often nothing at all, because a sky where every world is adorned has
// no emphasis left to spend.

const ADORNMENTS: Adornment[] = [
  "none", "none", "none", "moon", "halo", "tilted-pair", "rays",
];

export function planetLook(planetId: string, ringSlot: number | null): PlanetLook {
  const seed = hash32(planetId);
  const cls = planetClass(planetId, ringSlot);
  const colours = palette(planetId, ringSlot, cls);

  const adornment: Adornment =
    cls === "ringed"
      ? "rings"
      : cls === "giant"
        ? (["rings", "moon", "halo", "none"] as Adornment[])[draw(seed, "adorn-giant", 4)]
        : ADORNMENTS[draw(seed, "adorn", ADORNMENTS.length)];

  return {
    planetClass: cls,
    ...colours,
    motif: MOTIFS[draw(seed, "motif", MOTIFS.length)],
    adornment,
    tilt: -40 + draw(seed, "tilt", 80),
  };
}

/**
 * Drift animation parameters, also derived so a planet always sways the
 * same way.  Two directions and a spread of periods, exactly as the
 * reference does it — the sky should breathe, not pulse in unison.
 */
export function driftOf(planetId: string) {
  const seed = hash32(planetId);
  // Bounded by construction rather than by rounding a 0..1 draw: a
  // `toFixed` on 0.9999 lands exactly on the bound, and a delay equal to
  // the period is a planet that never staggers.
  return {
    direction: draw(seed, "drift", 2) === 0 ? "A" : "B",
    duration: 12 + draw(seed, "drift-dur", 5),
    delay: draw(seed, "drift-delay", 40) / 10,
    twinkleDelay: draw(seed, "twinkle", 34) / 10,
  };
}


// ── naming ───────────────────────────────────────────────────────────
//
// A sky of thirty-four worlds cannot wear thirty-four full titles: at
// sky zoom the labels are a few pixels tall and every one of them
// collides with its neighbours.  The reference field solved this by
// hand-authoring a SHORT name per planet alongside the long one — a
// table that cannot exist for a registry, where the next planet arrives
// when somebody uploads a taxonomy.
//
// So the short name is derived, by the same rule for every title: drop
// the words that carry no identity, then take as many of the rest as
// fit.  Ring zoom shows the authored title in full, which is what the
// extra room is for.

const FILLER = new Set([
  "a", "an", "and", "as", "at", "but", "by", "for", "from", "in", "into", "is",
  "it", "its", "not", "of", "on", "or", "that", "the", "to", "with", "your",
]);

const SHORT_MAX = 14;

export function shortLabel(title: string, fallback: string): string {
  const raw = (title || fallback || "").trim();
  if (!raw) return fallback;
  const words = raw.split(/\s+/).map((word) => word.replace(/[.,;:—–-]+$/u, ""));
  const carrying = words.filter((word) => word && !FILLER.has(word.toLowerCase()));
  const source = carrying.length ? carrying : words.filter(Boolean);
  if (!source.length) return raw;

  let label = source[0];
  for (const word of source.slice(1)) {
    const next = `${label} ${word}`;
    if (next.length > SHORT_MAX) break;
    label = next;
  }
  return label;
}
